/**
 * "Who can read this" (t167u). The API builds it from AccessService's one read-access rule; groups are the routes
 * by which people get access. Each person appears once, under their most direct route, with any other routes in
 * `also_via`.
 */
export type ReaderRoute = 'participants' | 'direct' | 'department' | 'group' | 'unit_building' | 'building' | 'workspace';
export interface ReaderPerson {
  user_id: string;
  display_name: string;
  avatar_path: string | null;
  job_title: string | null;
  /** Members only: has read up to the latest message (null when they aren't in it yet, or nothing's been posted). */
  read_latest: boolean | null;
  /** Other routes that would also give them access, e.g. "Building: Cromwell - Auction". */
  also_via: string[];
}
export interface ReaderGroup {
  key: string;
  route: ReaderRoute;
  label: string;
  /** People here can open and join to read (public channels) rather than already being in it. */
  joinable: boolean;
  people: ReaderPerson[];
}
export interface ConversationReaders {
  conversation_id: string;
  kind: 'dm' | 'group_dm' | 'channel' | 'room';
  is_private: boolean;
  /** Everyone who can read (in it now + can join). */
  total: number;
  /** In it now (can read straight away). */
  members: number;
  groups: ReaderGroup[];
}

/** Hover summary, e.g. "12 members · 40 via Cromwell - Auction · 25 more can join". */
export function readersSummary(r: ConversationReaders): string {
  const n = (x: number, one: string, many: string) => `${x} ${x === 1 ? one : many}`;
  return r.groups.map(g => g.route === 'direct' ? n(g.people.length, 'member', 'members')
    : g.route === 'participants' ? n(g.people.length, 'participant', 'participants')
    : g.route === 'building' || g.route === 'unit_building' || g.route === 'department' || g.route === 'group' ? `${g.people.length} via ${g.label.replace(/^Through [a-z ]+: /, '')}`
    : `${g.people.length} more can join`).join(' · ');
}

/** Search across groups by name or job title; empty groups are dropped. */
export function filterReaders(groups: ReaderGroup[], q: string): ReaderGroup[] {
  const t = q.trim().toLowerCase();
  if (!t) return groups;
  return groups.map(g => ({ ...g, people: g.people.filter(p => p.display_name.toLowerCase().includes(t) || (p.job_title ?? '').toLowerCase().includes(t)) })).filter(g => g.people.length);
}

/** t167u: a channel's linked units (Access). */
export interface ChannelAccessUnits { team_ids: string[]; location_ids: string[] }
/**
 * t449u: can this channel be deleted for good? Only workspace Owners see the option (`owner` false: hide it), and only an
 * empty channel (no posts, replies, files, polls, calls, pins or tasks; system lines don't count) that isn't the
 * workspace's default channel or linked to a project or something else. `reason` says why not, in plain words.
 */
export interface ChannelDeleteCheck { owner: boolean; can_delete: boolean; reason: 'not_owner' | 'not_channel' | 'default' | 'linked' | 'not_empty' | null; message: string | null }
